import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, AttributeDatatypeDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions, SimpleListSelectItem } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { TranslateService } from '@ngx-translate/core';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

type AllowedDeviceAttributes = 'DeviceAttribute' | 'LocationAttribute' | 'CompanyAttribute' | 'ProductAttribute' | 'MediumAttribute';
type SplitButtonAction = 'edit';
type AttributeDatatypeDTOAndButtons = AttributeDatatypeDTO & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-attributes',
  templateUrl: './attributes.component.html',
  styleUrls: ['./attributes.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributesComponent extends RequestClass implements OnInit {
  // injects
  private readonly api = inject(API);
  private readonly errorHandler = inject(ErrorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly navService = inject(NavigationService);
  protected readonly util = inject(UtilService);
  protected readonly stringTransformation = inject(StringTransformationService);
  protected readonly permissionService = inject(PermissionService);

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // type filter
  selectedType: AllowedDeviceAttributes = 'DeviceAttribute';
  // config
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: {
      visibleWithoutChanges: true,
    },
  };
  // items
  attributeTypes: SimpleListSelectItem[] = [];

  // global
  attributes: AttributeDatatypeDTOAndButtons[] = [];

  ngOnInit() {
    if (this.permissionService.hasPermission(this.permissionService.Permission.device_attribute_datatype_list)) {
      this.attributeTypes.push({
        value: 'DeviceAttribute',
        label: this.translate.instant('lang.globals.deviceAttribute'),
      });
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.location_attribute_datatype_list)) {
      this.attributeTypes.push({
        value: 'LocationAttribute',
        label: this.translate.instant('lang.globals.locationAttribute'),
      });
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.company_attribute_datatype_list)) {
      this.attributeTypes.push({
        value: 'CompanyAttribute',
        label: this.translate.instant('lang.globals.companyAttribute'),
      });
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.product_attribute_datatype_list)) {
      this.attributeTypes.push({
        value: 'ProductAttribute',
        label: this.translate.instant('lang.globals.productAttribute'),
      });
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.medium_group_attribute_datatype_list)) {
      this.attributeTypes.push({
        value: 'MediumAttribute',
        label: this.translate.instant('lang.globals.mediumAttribute'),
      });
    }
    if (this.attributeTypes.length <= 0) {
      this.navService.navigateToPoint(RoutingID.error, null, { queryParams: { error: 403 }, replaceUrl: true });
    }
    // set the selected Type to the first permitted attributeType
    this.selectedType = this.attributeTypes[0].value;
    this.requestDebounced();
  }

  creationAllowed(): boolean {
    return this.attributeTypes?.some(type => type.value === this.selectedType) ?? false;
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    let obs = null;

    switch (this.selectedType) {
      case 'DeviceAttribute':
        obs = this.api.ca_md_DeviceAttribute_retrieveAllDatatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          },
          this.pagination
        );
        break;
      case 'LocationAttribute':
        obs = this.api.ca_md_LocationAttribute_retrieveAllDatatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          },
          this.pagination
        );
        break;
      case 'CompanyAttribute':
        obs = this.api.ca_md_CompanyAttribute_retrieveAllDatatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          },
          this.pagination
        );
        break;
      case 'ProductAttribute':
        obs = this.api.ca_md_ProductAttribute_retrieveAllDatatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          },
          this.pagination
        );
        break;
      case 'MediumAttribute':
        obs = this.api.ca_md_MediumGroupAttribute_retrieveAllDatatypes(
          {
            search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          },
          this.pagination
        );
        break;
    }

    if (!obs) {
      this.attributes = [];
      return;
    }
    obs
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (data: AttributeDatatypeDTO[]) => {
          this.attributes = this.buildSplitButtons(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.device_attribute_datatype_edit])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.attributeEdit,
          pathParams: [{ id: 'attributeID', value: item?.id }],
          queryParams: { attrType: this.selectedType, returnTo: this.util.getPageLink() },
        });
      }
    });
    return result;
  }

  // filter methods
  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // type filter
    this.selectedType = filterMap.get('attrType')?.[0];
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
